import * as React from "react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

type Column<T> = {
  key: keyof T | string;
  header: React.ReactNode;
  className?: string;
  render?: (row: T, index: number) => React.ReactNode;
};

type ServerTableProps<T> = {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  rowKey?: (row: T, index: number) => React.Key;
  zebra?: boolean;
  containerClassName?: string;
  tableClassName?: string;
};

export function ServerTable<T>({
  columns,
  data,
  loading,
  emptyMessage = "No data found.",
  rowKey,
  zebra = true,
  containerClassName = "overflow-x-auto rounded-xl border border-border/60",
  tableClassName = "table-fixed",
}: ServerTableProps<T>) {
  return (
    <div className={containerClassName}>
      <div className="max-h-[420px] overflow-y-auto">
        <Table className={tableClassName}>
          <TableHeader className="sticky top-0 z-[1] bg-muted/60 backdrop-blur supports-[backdrop-filter]:bg-muted/40">
            <TableRow className="hover:bg-transparent">
              {columns.map((c, i) => (
                <TableHead key={i} className={`py-3 pl-4 pr-3 text-[12px] uppercase tracking-wide text-muted-foreground font-medium ${c.className ?? ""}`}>
                  {c.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-10 text-center text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-10 text-center text-muted-foreground">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, idx) => (
                <TableRow
                  key={rowKey ? rowKey(row, idx) : idx}
                  className={`${zebra && idx % 2 !== 0 ? "bg-muted/20" : "bg-background"} hover:bg-muted/40 transition-colors`}
                >
                  {columns.map((c, ci) => (
                    <TableCell key={ci} className={`py-3 pl-4 pr-3 align-middle text-sm text-foreground/90 ${c.className ?? ""}`}>
                      {c.render ? c.render(row, idx) : (row as any)[c.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
